import { IonList, IonText } from '@ionic/react';
import { ReactElement, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  InfiniteScrollOptions,
  useSearchListQueryWithInfiniteScroll,
} from '../../api/hooks/infinite-scroll.hooks';
import { LoadingRetry } from '../graphql/LoadingRetry';
import {
  Searchbar,
  SearchbarPopoverChildrenProps,
} from '../ui/searchbar/searchbar';

export interface RowProps<T = unknown> {
  item: T;
  index: number;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type RowRenderer<T = any> = (props: RowProps<T>) => ReactElement;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type RowFilter<T = any> = (props: RowProps<T>) => boolean;

interface ObjectListProps {
  query: InfiniteScrollOptions;
  filterRow?: RowFilter;
  noResults?: string | ReactElement;
  rowRenderer: RowRenderer;
  filterPopoverContent?: (props: SearchbarPopoverChildrenProps) => ReactElement;
  showFilterIndicator?: boolean;
}

const ObjectList = (props: ObjectListProps): ReactElement => {
  const {
    rowRenderer,
    query,
    filterRow,
    filterPopoverContent,
    showFilterIndicator,
  } = props;
  const { i18n } = useTranslation();
  const {
    infiniteScroll,
    result: { refetch, error, loading },
    search,
    items,
  } = useSearchListQueryWithInfiniteScroll(query);

  const filteredItems = useMemo(() => {
    if (filterRow) {
      return items.filter((item, index) => filterRow({ index, item }));
    } else {
      return items;
    }
  }, [filterRow, items]);

  let noResults = props.noResults;
  if (noResults === undefined || typeof noResults === 'string') {
    noResults = (
      <div className="ion-padding ion-text-center">
        <IonText color="medium">
          {noResults ?? i18n.t('common:no_results_found')}
        </IonText>
      </div>
    );
  }

  return (
    <>
      <IonList lines="full">
        <Searchbar
          onSearch={search}
          filterPopoverContent={filterPopoverContent}
          showFilterIndicator={showFilterIndicator}
        />
      </IonList>
      <LoadingRetry
        refetch={refetch}
        error={error}
        loading={loading}
        results={{
          hasResults: filteredItems.length > 0,
          noResultsComponent: noResults,
        }}
      >
        <IonList lines="full">
          {filteredItems.map((item, index) => rowRenderer({ item, index }))}
        </IonList>
        {infiniteScroll}
        <div style={{ height: 40 }} />
      </LoadingRetry>
    </>
  );
};

export { ObjectList };
